import React, { useState, useEffect, useMemo } from 'react';
import {
  Button,
  Select,
  MenuItem,
  InputLabel,
  FormControl,
  Stack,
  Box,
  Typography,
  CircularProgress,
  Alert,
  FormControlLabel,
  Switch,
  TextField,
} from '@mui/material';
import HdIcon from '@mui/icons-material/Hd';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import SettingsSuggestIcon from '@mui/icons-material/SettingsSuggest';
import { ConsoleDialogFrame } from '../console/ConsoleDialogFrame';

interface HLSModalProps {
  open: boolean;
  onClose: () => void;
  onSave: (data: any) => Promise<void>;
  mode: 'HLS_STANDARD' | 'HLS_SCTE35';
  initialData?: any;
}

const randomMount = () => `hls_${Date.now()}`;

const parseInitialDestination = (destination?: string) => {
  const raw = String(destination || '');
  if (!raw.startsWith('local_hls/')) {
    return {
      mount: randomMount(),
      abrEnabled: false,
      scte35Enabled: false,
      videoCodec: 'copy',
      audioCodec: 'copy',
    };
  }

  const encoded = raw.slice('local_hls/'.length);
  const [mountPart, queryPart = ''] = encoded.split('?');
  const params = new URLSearchParams(queryPart);

  return {
    mount: mountPart || randomMount(),
    abrEnabled: params.get('abr') === '1',
    scte35Enabled: params.get('scte35') === '1',
    videoCodec: params.get('vcodec') || 'copy',
    audioCodec: params.get('acodec') || 'copy',
  };
};

export const HLSModal: React.FC<HLSModalProps> = ({ open, onClose, onSave, mode, initialData }) => {
  const isPlus = mode === 'HLS_SCTE35';

  const [form, setForm] = useState({
    mount: randomMount(),
    videoCodec: 'copy',
    audioCodec: 'copy',
    videoBitrate: '',
    resolution: '',
    audioBitrate: '',
    keyframeInterval: '',
    abrEnabled: false,
    scte35Enabled: false,
  });

  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (!open) return;

    const parsed = parseInitialDestination(initialData?.destination);

    if (isPlus) {
      setForm({
        mount: parsed.mount,
        videoCodec: initialData?.videoCodec || parsed.videoCodec || 'copy',
        audioCodec: initialData?.audioCodec || parsed.audioCodec || 'copy',
        videoBitrate: initialData?.videoBitrate || '',
        resolution: initialData?.resolution || '',
        audioBitrate: initialData?.audioBitrate || '',
        keyframeInterval: initialData?.keyframeInterval || '',
        abrEnabled: parsed.abrEnabled,
        scte35Enabled: parsed.scte35Enabled || !!initialData?.enableScte35,
      });
    } else {
      setForm({
        mount: parsed.mount,
        videoCodec: 'copy',
        audioCodec: 'copy',
        videoBitrate: '',
        resolution: '',
        audioBitrate: '',
        keyframeInterval: '',
        abrEnabled: false,
        scte35Enabled: false,
      });
    }
  }, [open, initialData, isPlus]);

  const handleChange = (e: any) => {
    setForm((prev) => ({ ...prev, [e.target.name]: e.target.value }));
  };

  const previewUrl = useMemo(() => {
    return isPlus && form.abrEnabled
      ? `${window.location.origin}/hls-plus/[auto]/streamy.m3u8`
      : `${window.location.origin}/hls-plus/[auto]/index.m3u8`;
  }, [isPlus, form.abrEnabled]);

  const handleSubmit = async () => {
    setIsSubmitting(true);

    const params = new URLSearchParams();
    params.set('segmentDuration', '3');
    params.set('playlistLength', '5');

    if (isPlus) {
      params.set('vcodec', form.videoCodec || 'copy');
      params.set('acodec', form.audioCodec || 'copy');
      if (form.abrEnabled) params.set('abr', '1');
      if (form.scte35Enabled) params.set('scte35', '1');
    } else {
      params.set('vcodec', 'copy');
      params.set('acodec', 'copy');
    }

    const payload: any = isPlus
      ? {
          protocol: 'hls',
          destination: `local_hls/${form.mount}?${params.toString()}`,
          platformDistributor: 'HLS+',
          videoCodec: form.videoCodec || 'copy',
          audioCodec: form.audioCodec || 'copy',
          videoBitrate: form.videoBitrate || null,
          audioBitrate: form.audioBitrate || null,
          resolution: form.resolution || null,
          keyframeInterval: form.keyframeInterval || null,
          isHlsSpecial: false,
          enableScte35: form.scte35Enabled,
        }
      : {
          protocol: 'hls',
          destination: `local_hls/${form.mount}?${params.toString()}`,
          platformDistributor: 'HLS',
          videoCodec: 'copy',
          audioCodec: 'copy',
          videoBitrate: null,
          audioBitrate: null,
          resolution: null,
          keyframeInterval: null,
          isHlsSpecial: true,
          enableScte35: false,
        };

    if (initialData?.id) {
      payload.id = initialData.id;
    }

    try {
      await onSave(payload);
      onClose();
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <ConsoleDialogFrame
      open={open}
      onClose={onClose}
      maxWidth="sm"
      fullWidth
      icon={isPlus ? <AutoAwesomeIcon /> : <HdIcon />}
      title={isPlus ? 'New HLS+' : 'New HLS Output'}
      subtitle={isPlus
        ? 'Configure adaptive delivery, SCTE-35 markers, and codec behavior from the same console interaction surface.'
        : 'Create a standard HLS output using the platform house profile and managed backend delivery settings.'}
      actions={(
        <>
          <Button onClick={onClose} disabled={isSubmitting} sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}>
            Cancel
          </Button>
          <Button
            variant="contained"
            onClick={handleSubmit}
            disabled={isSubmitting}
            color={isPlus ? 'secondary' : 'primary'}
            sx={{ borderRadius: 0, textTransform: 'none', fontWeight: 800 }}
          >
            {isSubmitting ? <CircularProgress size={24} /> : 'Save Output'}
          </Button>
        </>
      )}
    >
      <Stack spacing={2.5} sx={{ mt: 1 }}>
        <Box
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            bgcolor: 'background.default',
            px: 1.5,
            py: 1.2,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1,
          }}
        >
          <Box>
            <Typography sx={{ fontSize: '0.68rem', fontWeight: 900, letterSpacing: '0.08em', textTransform: 'uppercase', color: 'text.secondary' }}>
              Delivery Profile
            </Typography>
            <Typography variant="body2" fontWeight={800}>
              {isPlus ? 'HLS+' : 'Standard HLS'}
            </Typography>
          </Box>
          <SettingsSuggestIcon sx={{ color: isPlus ? 'secondary.main' : 'primary.main' }} />
        </Box>

        {!isPlus ? (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            Standard HLS is a direct copy of the source feed turned into m3u8. It uses a fixed backend profile.
          </Alert>
        ) : (
          <Alert severity="info" sx={{ borderRadius: 0 }}>
            HLS+ supports ABR and advanced codec and bitrate controls. Segment count and playlist length are fixed by backend.
          </Alert>
        )}

        <TextField
          label="Generated Link"
          value={previewUrl}
          fullWidth
          size="small"
          InputProps={{ readOnly: true }}
        />

        {isPlus && (
          <>
            <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
              <FormControlLabel
                control={
                  <Switch
                    checked={form.abrEnabled}
                    onChange={(e) => setForm((prev) => ({ ...prev, abrEnabled: e.target.checked }))}
                  />
                }
                label="Enable ABR"
              />
              <FormControlLabel
                control={
                  <Switch
                    checked={form.scte35Enabled}
                    onChange={(e) => setForm((prev) => ({ ...prev, scte35Enabled: e.target.checked }))}
                  />
                }
                label="Enable SCTE35 markers"
              />
            </Stack>

            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Resolution</InputLabel>
                <Select
                  name="resolution"
                  value={form.resolution}
                  label="Resolution"
                  onChange={handleChange}
                >
                  <MenuItem value="">Copy source</MenuItem>
                  <MenuItem value="1080">1080p</MenuItem>
                  <MenuItem value="720">720p</MenuItem>
                  <MenuItem value="360">360p</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Video Bitrate</InputLabel>
                <Select
                  name="videoBitrate"
                  value={form.videoBitrate}
                  label="Video Bitrate"
                  onChange={handleChange}
                >
                  <MenuItem value="">Copy/source default</MenuItem>
                  <MenuItem value="6M">6 Mbps</MenuItem>
                  <MenuItem value="4M">4 Mbps</MenuItem>
                  <MenuItem value="2.5M">2.5 Mbps</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Video Codec</InputLabel>
                <Select
                  name="videoCodec"
                  value={form.videoCodec}
                  label="Video Codec"
                  onChange={handleChange}
                >
                  <MenuItem value="copy">Copy</MenuItem>
                  <MenuItem value="libx264">H264</MenuItem>
                  <MenuItem value="libx265">H.265</MenuItem>
                </Select>
              </FormControl>

              <FormControl fullWidth size="small">
                <InputLabel>Audio Codec</InputLabel>
                <Select
                  name="audioCodec"
                  value={form.audioCodec}
                  label="Audio Codec"
                  onChange={handleChange}
                >
                  <MenuItem value="copy">Copy</MenuItem>
                  <MenuItem value="aac">AAC</MenuItem>
                  <MenuItem value="libopus">Opus</MenuItem>
                </Select>
              </FormControl>
            </Box>

            <Box display="flex" gap={2}>
              <FormControl fullWidth size="small">
                <InputLabel>Audio Bitrate</InputLabel>
                <Select
                  name="audioBitrate"
                  value={form.audioBitrate}
                  label="Audio Bitrate"
                  onChange={handleChange}
                >
                  <MenuItem value="">Copy/source default</MenuItem>
                  <MenuItem value="128k">128 kbps</MenuItem>
                  <MenuItem value="96k">96 kbps</MenuItem>
                  <MenuItem value="64k">64 kbps</MenuItem>
                </Select>
              </FormControl>

              <TextField
                label="Keyframe Interval (s)"
                name="keyframeInterval"
                value={form.keyframeInterval}
                onChange={handleChange}
                fullWidth
                size="small"
                placeholder="Optional"
              />
            </Box>
          </>
        )}
      </Stack>
    </ConsoleDialogFrame>
  );
};
